import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router";
import { Icon } from "@/components/common/Icon";
import { nextMenuItemIndex } from "@/lib/menu-keyboard";

export type ContextMenuPosition =
	| {
			/** Anchor element. Popup is placed below the anchor's bounding rect. */
			anchor: HTMLElement;
			/** Which edge of the popup aligns with the matching edge of the anchor. Default "right". */
			align?: "left" | "right";
			/** Vertical gap between the anchor's bottom and the popup. Default 4. */
			offset?: number;
	  }
	| {
			x: number;
			y: number;
			/**
			 * "right" anchors the popup's right edge at `x` (popup extends to the left of the point).
			 * "left" anchors the popup's left edge at `x` (popup extends to the right of the point).
			 */
			align?: "left" | "right";
	  };

export interface ContextMenuItem {
	/** Stable key. Defaults to `label`. */
	key?: string;
	label: string;
	/** ラベルの一部に色やアイコンを付ける場合の表示内容。 */
	content?: ReactNode;
	/** Called when the item is clicked. The menu closes automatically afterwards. */
	onSelect?: () => void;
	/** false のときは選択後もメニューを閉じない。 */
	closeOnSelect?: boolean;
	/** 破壊的な操作 (削除など) を赤字で強調する。 */
	destructive?: boolean;
	disabled?: boolean;
	/** ツールチップ。ラベルだけでは分からない情報 (無効化の理由など) がある場合のみ設定する。 */
	title?: string;
	/** ラベル右側に薄く表示するキーボードショートカット表記 (例: "⌘S")。 */
	shortcut?: string;
	/** If true, allows checking and unchecking the item */
	selectable?: boolean;
	/** false のときは選択状態を示すチェック欄を表示しない。 */
	showSelectionCheck?: boolean;
	selected?: boolean;
	/** ホバー時に表示するサブメニュー。 */
	subMenu?: readonly ContextMenuEntry[] | readonly ContextMenuGroup[];
	/** サブメニューの展開方向。既定は横 (行の脇へのポップアップ)。inline は行の上へのアコーディオン展開。 */
	subMenuDirection?: "horizontal" | "inline";
	/** サブメニューの開閉状態を示すアイコンを表示する。 */
	subMenuToggleIcon?: boolean;
	/** true のとき、ホバーでは開かずクリックでサブメニューを開く。 */
	subMenuOpenOnClick?: boolean;
	subMenuMinWidth?: number;
	/**
	 * When set, the item renders as a react-router `<Link to={routerLink}>` instead of a button,
	 * so middle-click / ⌘-click / right-click → "open in new tab" all work. `onSelect` still
	 * fires on plain left-click before the menu closes.
	 */
	routerLink?: string;
	/** routerLink を新しいタブで開く。 */
	openInNewTab?: boolean;
	testId?: string;
}

export interface ContextMenuSeparator {
	separator: true;
}

export type ContextMenuEntry = ContextMenuItem | ContextMenuSeparator;

export interface ContextMenuGroup {
	/** Optional header label rendered above the group's items. */
	label?: string;
	items: readonly ContextMenuEntry[];
}

interface ContextMenuProps {
	position: ContextMenuPosition;
	onClose: () => void;
	/** Flat list of items, or list of groups with optional headers. */
	items: readonly ContextMenuEntry[] | readonly ContextMenuGroup[];
	testId?: string;
	/** Minimum popup width in px. Default 160. */
	minWidth?: number;
	/** Where the selection checkmark renders relative to the label. Default "leading". */
	checkAlign?: "leading" | "trailing";
	/** 親メニュー項目内に埋め込むサブメニューとして表示する (値は展開方向)。 */
	embedded?: "horizontal" | "inline";
}

function isSeparator(entry: ContextMenuEntry): entry is ContextMenuSeparator {
	return "separator" in entry;
}

function isGroupList(
	list: readonly ContextMenuEntry[] | readonly ContextMenuGroup[],
): list is readonly ContextMenuGroup[] {
	return list.length > 0 && "items" in list[0];
}

/** 埋め込みサブメニューは親項目に相対配置されるため、position は使われない。 */
const EMBEDDED_POSITION: ContextMenuPosition = { x: 0, y: 0 };

/** ↑↓ で巡回できる項目 (無効でないもの)。開いているサブメニューの項目も DOM 順に含む。 */
const MENU_ITEM_SELECTOR = "[data-menu-item]:not([disabled])";

export function ContextMenu({
	position,
	onClose,
	items,
	testId,
	minWidth = 160,
	checkAlign = "leading",
	embedded,
}: ContextMenuProps) {
	const menuRef = useRef<HTMLDivElement>(null);
	const [placement, setPlacement] = useState<{
		top: number;
		left: number;
		above: boolean;
	} | null>(null);
	const [openSubMenuKey, setOpenSubMenuKey] = useState<string | null>(null);
	// フォーカス (キーボード) とホバー (マウス) を同じ state で扱うと、両者が別要素で
	// 同時に発生した際にどちらのハイライトを消すべきか判断できなくなるため分けて持つ。
	const [hoveredKey, setHoveredKey] = useState<string | null>(null);
	const [focusedKey, setFocusedKey] = useState<string | null>(null);

	useEffect(() => {
		// 埋め込みサブメニューの開閉は親メニューが管理するため、グローバルリスナーは最上位だけが張る。
		if (embedded) return;
		const onPointerDown = (e: PointerEvent) => {
			const target = e.target as Node;
			if (menuRef.current?.contains(target)) return;
			if ("anchor" in position && position.anchor.contains(target)) return;
			onClose();
		};
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				onClose();
				return;
			}
			if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
			const menuItems = Array.from(
				menuRef.current?.querySelectorAll<HTMLElement>(MENU_ITEM_SELECTOR) ?? [],
			);
			if (menuItems.length === 0) return;
			e.preventDefault();
			const current = menuItems.indexOf(document.activeElement as HTMLElement);
			const step = e.key === "ArrowDown" ? 1 : -1;
			menuItems[nextMenuItemIndex(current, menuItems.length, step)].focus();
		};
		window.addEventListener("pointerdown", onPointerDown);
		window.addEventListener("keydown", onKey);
		return () => {
			window.removeEventListener("pointerdown", onPointerDown);
			window.removeEventListener("keydown", onKey);
		};
	}, [embedded, onClose, position]);

	useLayoutEffect(() => {
		const menu = menuRef.current;
		if (!menu || embedded) return;
		const margin = 8;
		// カーソル起点のメニューは右側へ、ボタン起点のメニューは従来どおり右端揃えで表示する。
		const align = position.align ?? ("anchor" in position ? "right" : "left");

		const compute = () => {
			// 出現アニメの scale を含まない寸法で測る。
			const menuRect = { width: menu.offsetWidth, height: menu.offsetHeight };
			const vw = window.innerWidth;
			const vh = window.innerHeight;

			let top: number;
			let left: number;
			let above = false;

			if ("anchor" in position) {
				const rect = position.anchor.getBoundingClientRect();
				const offset = position.offset ?? 4;
				top = rect.bottom + offset;
				left = align === "right" ? rect.right - menuRect.width : rect.left;
				if (top + menuRect.height > vh - margin) {
					const aboveTop = rect.top - offset - menuRect.height;
					above = aboveTop >= margin;
					top = above ? aboveTop : Math.max(margin, vh - menuRect.height - margin);
				}
			} else {
				top = position.y;
				left = align === "right" ? position.x - menuRect.width : position.x;
				if (top + menuRect.height > vh - margin) {
					top = Math.max(margin, vh - menuRect.height - margin);
				}
			}

			if (left + menuRect.width > vw - margin) left = vw - menuRect.width - margin;
			if (left < margin) left = margin;

			setPlacement({ top, left, above });
		};

		compute();
		window.addEventListener("resize", compute);
		return () => window.removeEventListener("resize", compute);
	}, [embedded, position, items]);

	const groups: readonly ContextMenuGroup[] = isGroupList(items) ? items : [{ items }];
	const hasSelectionSlot = groups.some((g) => g.items.some((i) => !isSeparator(i) && i.selectable));

	const popupChrome: React.CSSProperties = {
		zIndex: 100,
		minWidth,
		padding: 4,
		background: "var(--surface-1)",
		borderRadius: "var(--radius-6)",
		boxShadow: "var(--shadow-menu)",
	};
	const containerStyle: React.CSSProperties =
		embedded === "inline"
			? { minWidth }
			: embedded === "horizontal"
				? { ...popupChrome, position: "absolute", bottom: 0, right: "calc(100% + 5px)" }
				: {
						...popupChrome,
						position: "fixed",
						top: placement?.top ?? 0,
						left: placement?.left ?? 0,
						visibility: placement ? "visible" : "hidden",
					};

	return (
		<div
			ref={menuRef}
			data-testid={testId}
			className={embedded ? undefined : "anchor" in position ? "izanagi-dropdown" : "izanagi-popup"}
			data-placement={placement?.above ? "above" : undefined}
			style={containerStyle}
		>
			{groups.map((group, groupIdx) => (
				<div
					key={group.label ?? `group-${groupIdx}`}
					style={{ display: "flex", flexDirection: "column", gap: 2 }}
				>
					{group.label && (
						<div
							style={{
								padding: "6px 12px 2px",
								fontSize: "var(--text-section)",
								fontWeight: 600,
								letterSpacing: "var(--tracking-label)",
								textTransform: "uppercase",
								color: "var(--fg-3)",
							}}
						>
							{group.label}
						</div>
					)}
					{group.items.map((entry, entryIdx) => {
						if (isSeparator(entry)) {
							return (
								<div
									key={`sep-${entryIdx}`}
									style={{ height: 1, background: "var(--line-1)", margin: "4px 0" }}
								/>
							);
						}
						const item = entry;
						const key = item.key ?? item.label;
						const disabled = item.disabled ?? false;
						const isHighlighted = !disabled && (hoveredKey === key || focusedKey === key);
						const isSelected = item.selected ?? false;
						const itemStyle: React.CSSProperties = {
							display: "flex",
							alignItems: "center",
							gap: 8,
							width: "100%",
							padding: "8px 12px",
							background: isSelected
								? isHighlighted
									? "var(--menu-selected-hover-bg)"
									: "var(--menu-selected-bg)"
								: isHighlighted
									? "var(--menu-hover-bg)"
									: "transparent",
							border: 0,
							borderRadius: "var(--radius-5)",
							cursor: disabled ? "not-allowed" : "pointer",
							fontSize: "var(--text-caption)",
							fontWeight: isSelected ? 600 : 500,
							color: disabled
								? "var(--fg-3)"
								: item.destructive
									? "var(--danger)"
									: isSelected || isHighlighted
										? "var(--fg-0)"
										: "var(--fg-2)",
							transition: "background 120ms ease, color 120ms ease",
							textAlign: "left",
							opacity: disabled ? 0.6 : 1,
							textDecoration: "none",
							fontFamily: "inherit",
						};
						const onItemMouseEnter = () => {
							if (disabled) return;
							setHoveredKey(key);
							if (!item.subMenuOpenOnClick) {
								setOpenSubMenuKey(item.subMenu ? key : null);
							}
						};
						const onItemMouseLeave = () => {
							setHoveredKey((k) => (k === key ? null : k));
						};
						const onItemFocus = () => {
							if (!disabled) setFocusedKey(key);
						};
						const onItemBlur = () => {
							setFocusedKey((k) => (k === key ? null : k));
						};
						const onItemClick = () => {
							if (item.subMenuOpenOnClick && item.subMenu) {
								setOpenSubMenuKey((openKey) => (openKey === key ? null : key));
								return;
							}
							item.onSelect?.();
							if (item.closeOnSelect !== false) {
								if ("anchor" in position) position.anchor.focus();
								onClose();
							}
						};
						const checkSlot = hasSelectionSlot && item.showSelectionCheck !== false && (
							<span
								style={{
									width: 14,
									height: 14,
									display: "grid",
									placeItems: "center",
									flexShrink: 0,
								}}
							>
								{item.selected && <Icon name="check" size={12} color="var(--fg-0)" />}
							</span>
						);
						const labelSlot = (
							<span style={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0 }}>
								{item.content ?? item.label}
							</span>
						);
						const subMenuToggleIcon = item.subMenuToggleIcon && item.subMenu && (
							<Icon
								name={
									openSubMenuKey === key
										? item.subMenuDirection === "inline"
											? "chevronU"
											: "chevronD"
										: "chevronR"
								}
								size={12}
								color="var(--fg-3)"
							/>
						);
						const shortcutSlot = item.shortcut && (
							<span
								style={{
									fontSize: "var(--text-caption)",
									color: "var(--fg-3)",
									flexShrink: 0,
								}}
							>
								{item.shortcut}
							</span>
						);
						const inner =
							checkAlign === "trailing" ? (
								<>
									{labelSlot}
									{shortcutSlot}
									{checkSlot}
									{subMenuToggleIcon}
								</>
							) : (
								<>
									{checkSlot}
									{labelSlot}
									{shortcutSlot}
									{subMenuToggleIcon}
								</>
							);
						let menuItem: ReactNode;
						if (item.routerLink !== undefined && !disabled) {
							menuItem = (
								<Link
									to={item.routerLink}
									target={item.openInNewTab ? "_blank" : undefined}
									data-menu-item=""
									data-testid={item.testId}
									onClick={onItemClick}
									onMouseEnter={onItemMouseEnter}
									onMouseLeave={onItemMouseLeave}
									onFocus={onItemFocus}
									onBlur={onItemBlur}
									style={itemStyle}
								>
									{inner}
								</Link>
							);
						} else {
							menuItem = (
								<button
									type="button"
									disabled={disabled}
									title={item.title}
									data-menu-item=""
									data-testid={item.testId}
									onClick={onItemClick}
									onMouseEnter={onItemMouseEnter}
									onMouseLeave={onItemMouseLeave}
									onFocus={onItemFocus}
									onBlur={onItemBlur}
									style={itemStyle}
								>
									{inner}
								</button>
							);
						}
						const subMenuDirection = item.subMenuDirection ?? "horizontal";
						const subMenuNode = item.subMenu && openSubMenuKey === key && (
							<>
								{subMenuDirection === "horizontal" && (
									<div
										style={{
											position: "absolute",
											top: 0,
											bottom: 0,
											right: "100%",
											width: 5,
										}}
									/>
								)}
								<ContextMenu
									embedded={subMenuDirection}
									position={EMBEDDED_POSITION}
									onClose={() => {
										setOpenSubMenuKey(null);
										onClose();
									}}
									items={item.subMenu}
									minWidth={item.subMenuMinWidth ?? minWidth}
									checkAlign={checkAlign}
								/>
							</>
						);
						return (
							<div
								key={key}
								style={{ position: "relative" }}
								onMouseLeave={() => {
									if (item.subMenu && !item.subMenuOpenOnClick) setOpenSubMenuKey(null);
								}}
							>
								{/* inline は行の上側へ展開する。下端固定メニューでも開閉でクリックした行が
								    カーソルの下から動かず、誤った mouseleave で親メニューが閉じない。 */}
								{subMenuDirection === "inline" ? (
									<>
										{subMenuNode}
										{menuItem}
									</>
								) : (
									<>
										{menuItem}
										{subMenuNode}
									</>
								)}
							</div>
						);
					})}
				</div>
			))}
		</div>
	);
}
